import { useEffect, useRef } from 'react';

// ----------------------------------------------------------------------

// Lets the browser / mouse Back button close an open overlay (dialog, drawer,
// lightbox) instead of navigating away from the page.
//
// Each open overlay pushes a same-URL history entry and joins a stack, so Back
// closes only the topmost one. When an overlay is closed some other way (X,
// Cancel, backdrop) its entry is removed with history.back(), and the popstate
// that causes is ignored so it can't close the overlay underneath.

const stack = [];
let ignorePops = 0;
let listening = false;

function handlePopState() {
  if (ignorePops > 0) {
    ignorePops -= 1;
    return;
  }
  const top = stack.pop();
  if (top) top.closeFromBack();
}

export function useBackToClose(open, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    if (!listening) {
      window.addEventListener('popstate', handlePopState);
      listening = true;
    }

    const { pathname } = window.location;
    const entry = {
      popped: false,
      closeFromBack: () => {
        entry.popped = true;
        onCloseRef.current?.();
      },
    };

    window.history.pushState(window.history.state, '');
    stack.push(entry);

    return () => {
      const i = stack.indexOf(entry);
      if (i >= 0) stack.splice(i, 1);
      // Closed without Back: drop our history entry, unless the user navigated away.
      if (!entry.popped && window.location.pathname === pathname) {
        ignorePops += 1;
        window.history.back();
      }
    };
  }, [open]);
}
